/**
 * <footer-newsletter> — footer newsletter form. Submits to Klaviyo via the
 * client subscriptions endpoint (`/client/subscriptions`). This is the ONLY
 * submission flow: the markup is a plain <form>, so there is no native/Shopify
 * submit and no page refresh. If the merchant has not configured the Klaviyo
 * public API key + list id, the form shows the "unavailable" message instead of
 * submitting.
 *
 * NOTE: this uses Klaviyo's PUBLIC company id (public API key / site id) only,
 * not a private API key. The private key MUST NOT be exposed client-side.
 *
 * Loaded ONLY from `blocks/footer-newsletter.liquid` via
 * `type="module" fetchpriority="low"`.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/form-validator
 */

import { Delegate } from '@theme/delegate';
import { FormValidator } from '@theme/form-validator';
import { klaviyoSignupProperties } from '@theme/klaviyo-signup-context';

class FooterNewsletter extends HTMLElement {
    constructor() {
        super();
        this.initialized = false;
        this.delegateElement = null;
        this.newsletterAbortControllers = new Map();
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.delegateElement = new Delegate(this);
        this.delegateElement.on(
            'submit',
            '.main-footer__newsletter-customer-form',
            this.#onNewsletterSubmit,
        );
        this.delegateElement.on(
            'click',
            '.main-footer__newsletter-clear-button',
            this.#onClearClick,
        );
        this.delegateElement.on(
            'focusout',
            '.main-footer__newsletter-row input[type="email"]',
            this.#onEmailBlur,
        );
        this.delegateElement.on(
            'input',
            '.main-footer__newsletter-row input[type="email"]',
            this.#onEmailInput,
        );

        this.#setupValidators();
    }

    // Shows the input error state on blur and keeps the submit button disabled
    // until a valid email is entered. The error class is toggled on the row so it
    // drives the existing red-underline + SEND→CLEAR styling. The message text
    // itself is rendered separately (see #onEmailBlur / #renderEmailError).
    #setupValidators() {
        this.querySelectorAll('.main-footer__newsletter-customer-form').forEach((form) => {
            new FormValidator(form, {
                fieldSelector: '.main-footer__newsletter-row',
                errorClass: 'main-footer__newsletter-row--error',
                buttonSelector: '.main-footer__newsletter-button',
                validateOnBlur: true,
                validateOnInput: false,
            });
        });
    }

    // FormValidator drives the row error class + button-disable, but its built-in
    // error output relies on the browser's validationMessage — which is empty for
    // inputs HTML5 considers valid (e.g. "name@gm", no dot required). So the
    // localized message is rendered here instead, in sync with the blur state.
    #isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    #renderEmailError(form, message) {
        const errorElement = form.querySelector('.main-footer__newsletter-error');
        if (errorElement) errorElement.textContent = message;
    }

    #onEmailBlur = (event, input) => {
        const form = input.closest('.main-footer__newsletter-customer-form');
        if (!form) return;

        const value = input.value.trim();
        const message =
            value && !this.#isValidEmail(value)
                ? this.#getFormConfig(form).invalidEmailMessage
                : '';
        this.#renderEmailError(form, message);
    };

    #onEmailInput = (event, input) => {
        const form = input.closest('.main-footer__newsletter-customer-form');
        if (!form) return;

        const value = input.value.trim();
        if (!value || this.#isValidEmail(value)) {
            this.#renderEmailError(form, '');
        }
    };

    #onClearClick = (event, button) => {
        event.preventDefault();
        const form = button.closest('.main-footer__newsletter-customer-form');
        if (!form) return;

        const emailInput = form.querySelector('input[name="contact[email]"]');
        if (emailInput) {
            emailInput.value = '';
            emailInput.focus();
            // Let FormValidator re-evaluate (clear error class, re-disable button).
            emailInput.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Remove the message element so the :has(...--error) state CSS deactivates
        // and the row returns to its neutral colors.
        form.querySelector('.main-footer__newsletter-message')?.remove();
    };

    disconnectedCallback() {
        this.initialized = false;
        this.delegateElement?.off();
        this.delegateElement = null;
        this.abortNewsletterRequests();
    }

    abortNewsletterRequests() {
        this.newsletterAbortControllers.forEach((controller) => controller.abort());
        this.newsletterAbortControllers.clear();
    }

    #refreshNewsletterAbortController(form) {
        this.newsletterAbortControllers.get(form)?.abort();

        const controller = new AbortController();
        this.newsletterAbortControllers.set(form, controller);

        return controller;
    }

    #isActiveNewsletterRequest(form, controller) {
        return (
            this.isConnected &&
            form?.isConnected &&
            !controller.signal.aborted &&
            this.newsletterAbortControllers.get(form) === controller
        );
    }

    #setNewsletterMessage(form, message, isError = false) {
        let messageElement = form.querySelector('.main-footer__newsletter-message');
        if (!messageElement) {
            messageElement = document.createElement('p');
            messageElement.className = 'main-footer__newsletter-message';
            // Insert right after the input row so the message sits between the row
            // and the terms note (instead of at the end of the form).
            const newsletterRow = form.querySelector('.main-footer__newsletter-row');
            if (newsletterRow) {
                newsletterRow.after(messageElement);
            } else {
                form.appendChild(messageElement);
            }
        }
        if (isError) {
            messageElement.setAttribute('role', 'alert');
            messageElement.setAttribute('aria-live', 'assertive');
        } else {
            messageElement.setAttribute('role', 'status');
            messageElement.setAttribute('aria-live', 'polite');
        }
        messageElement.setAttribute('aria-atomic', 'true');
        messageElement.textContent = message;
        messageElement.classList.toggle('main-footer__newsletter-message--error', isError);
    }

    #setNewsletterLoadingState(form, isLoading) {
        const submitButton = form.querySelector('.main-footer__newsletter-button');
        if (submitButton) {
            submitButton.disabled = isLoading;
            submitButton.setAttribute('aria-busy', String(isLoading));
        }
    }

    #showNewsletterSuccess(form, message) {
        const successText = form.querySelector('.main-footer__newsletter-success-text');
        if (successText) {
            successText.textContent = message;
            successText.hidden = false;
            successText.setAttribute('role', 'status');
            successText.setAttribute('aria-live', 'polite');
            successText.setAttribute('aria-atomic', 'true');
        }
        form.classList.add('is-success');
        // Remove any stray error message left over from a prior failed submit so
        // the row's error coloring deactivates and the success state owns the UI.
        form.querySelector('.main-footer__newsletter-message')?.remove();
    }

    #isAlreadySubscribedPayload(responseText = '') {
        if (!responseText) return false;

        const hasAlreadySubscribedText = (text = '') => {
            const normalized = text.toLowerCase();
            return (
                normalized.includes('already subscribed') ||
                normalized.includes('already in list') ||
                normalized.includes('already exists') ||
                normalized.includes('duplicate') ||
                normalized.includes('already consented')
            );
        };

        try {
            const parsed = JSON.parse(responseText);
            const errors = Array.isArray(parsed?.errors) ? parsed.errors : [];
            const errorText = errors
                .map((item) => [item?.code, item?.title, item?.detail].filter(Boolean).join(' '))
                .join(' ');
            return hasAlreadySubscribedText(errorText);
        } catch {
            return hasAlreadySubscribedText(responseText);
        }
    }

    #getFormConfig(form) {
        return {
            klaviyoEnabled:
                form.querySelector('.main-footer__klaviyo-enabled')?.value?.trim() === 'true',
            apiKey:
                form.querySelector('.main-footer__klaviyo-api-key')?.value?.trim() || '',
            listKey:
                form.querySelector('.main-footer__klaviyo-list-key')?.value?.trim() || '',
            successMessage:
                form.querySelector('.main-footer__newsletter-success-message')?.value?.trim() || '',
            alreadySubscribedMessage:
                form.querySelector('.main-footer__newsletter-already-message')?.value?.trim() || '',
            unavailableMessage:
                form.querySelector('.main-footer__newsletter-unavailable-message')?.value?.trim() || '',
            invalidEmailMessage:
                form.querySelector('.main-footer__newsletter-invalid-message')?.value?.trim() || '',
            requestFailedMessage:
                form.querySelector('.main-footer__newsletter-request-failed-message')?.value?.trim() || '',
        };
    }

    #getKlaviyoPayload(email, listId) {
        return {
            data: {
                type: 'subscription',
                attributes: {
                    custom_source: 'Shopify Footer Newsletter',
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: {
                                email,
                                // Which market and language the visitor signed up from - the
                                // same properties the popup and the notify-me form send.
                                properties: klaviyoSignupProperties(),
                                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                            },
                        },
                    },
                },
                relationships: { list: { data: { type: 'list', id: listId } } },
            },
        };
    }

    // Klaviyo's public client endpoint accepts CORS requests authorized by the
    // PUBLIC company id (`company_id` query param) — no private key needed. A 202
    // means the subscription was accepted; 409 (or an "already subscribed" error
    // body) means the profile is already on the list.
    async #subscribeViaKlaviyo(apiKey, listKey, email, signal) {
        const requestUrl = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(apiKey)}`;

        const response = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/vnd.api+json',
                Accept: 'application/vnd.api+json',
                revision: '2025-04-15',
            },
            body: JSON.stringify(this.#getKlaviyoPayload(email, listKey)),
            signal,
        });

        const responseText = await response.text();
        return { response, responseText };
    }

    #getSubscribedEmailsStorageKey(scope) {
        return `main-footer-subscribed:${scope}`;
    }

    #getStoredSubscribedEmails(scope) {
        try {
            const rawValue = window.localStorage.getItem(this.#getSubscribedEmailsStorageKey(scope));
            if (!rawValue) return [];
            const parsed = JSON.parse(rawValue);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    #isEmailStoredAsSubscribed(scope, email) {
        const normalizedEmail = email.toLowerCase();
        return this.#getStoredSubscribedEmails(scope).includes(normalizedEmail);
    }

    #storeSubscribedEmail(scope, email) {
        const normalizedEmail = email.toLowerCase();
        const existingEmails = this.#getStoredSubscribedEmails(scope);
        if (existingEmails.includes(normalizedEmail)) return;

        try {
            window.localStorage.setItem(
                this.#getSubscribedEmailsStorageKey(scope),
                JSON.stringify([...existingEmails, normalizedEmail]),
            );
        } catch {
            // Ignore storage quota/privacy mode issues and proceed silently.
        }
    }

    #onNewsletterSubmit = async (event, form) => {
        // Always prevent the native submit — this form is Klaviyo-only and must
        // never trigger a page refresh.
        event.preventDefault();

        const formConfig = this.#getFormConfig(form);
        if (!formConfig.klaviyoEnabled || !formConfig.apiKey || !formConfig.listKey) {
            this.#setNewsletterMessage(form, formConfig.unavailableMessage, true);
            return;
        }

        const emailInput = form.querySelector('input[name="contact[email]"]');
        const email = emailInput?.value?.trim();
        if (!email || !emailInput?.checkValidity?.() || !this.#isValidEmail(email)) {
            this.#setNewsletterMessage(form, formConfig.invalidEmailMessage, true);
            return;
        }

        const storageScope = `klaviyo:${formConfig.listKey}`;
        if (this.#isEmailStoredAsSubscribed(storageScope, email)) {
            this.#showNewsletterSuccess(form, formConfig.alreadySubscribedMessage);
            if (emailInput) emailInput.value = '';
            return;
        }

        const controller = this.#refreshNewsletterAbortController(form);
        this.#setNewsletterLoadingState(form, true);

        try {
            const { response, responseText } = await this.#subscribeViaKlaviyo(
                formConfig.apiKey,
                formConfig.listKey,
                email,
                controller.signal,
            );
            if (!this.#isActiveNewsletterRequest(form, controller)) return;

            const isAlreadySubscribed =
                response.status === 409 || this.#isAlreadySubscribedPayload(responseText);

            if (response.ok || isAlreadySubscribed) {
                this.#storeSubscribedEmail(storageScope, email);
                this.#showNewsletterSuccess(
                    form,
                    isAlreadySubscribed ? formConfig.alreadySubscribedMessage : formConfig.successMessage,
                );
                if (emailInput) emailInput.value = '';
                return;
            }

            throw new Error(`Klaviyo request failed with status ${response.status}`);
        } catch (error) {
            if (error.name === 'AbortError') return;
            if (!this.#isActiveNewsletterRequest(form, controller)) return;

            this.#setNewsletterMessage(form, formConfig.requestFailedMessage, true);
            console.error('[footer-newsletter / Klaviyo] Submit failed', error);
        } finally {
            if (this.newsletterAbortControllers.get(form) === controller) {
                this.newsletterAbortControllers.delete(form);

                if (this.isConnected && form.isConnected) {
                    this.#setNewsletterLoadingState(form, false);
                }
            }
        }
    };
}

if (!customElements.get('footer-newsletter')) {
    customElements.define('footer-newsletter', FooterNewsletter);
}
